/**
 * Display-boundary scrubber for vendor-authored error text (#103).
 *
 * Payer rejections carry pass-through text from the eligibility vendor's API
 * (AAA `rejectReason` / `possibleResolutions`, warning descriptions). That
 * text can name the vendor, link to vendor docs, and reference raw API field
 * names (`provider.npi`, `portalPassword`) that clinical staff can't act on.
 *
 * Applied at render time so historical check docs (which store the raw text)
 * are covered too. The stored data is untouched — this is UI copy hygiene,
 * not data sanitation.
 *
 * Scrubbing can reduce a string to empty (e.g. input was only a vendor URL) —
 * callers must gate rendering on the SCRUBBED value, not the raw one.
 */

/** Raw API field identifiers → plain language staff can act on. Order matters: paired-credential patterns before their single-field fallbacks. */
const FIELD_NAME_REPLACEMENTS: [RegExp, string][] = [
  [/provider\.npi\b/gi, "the provider's NPI"],
  [/provider\.federalTaxpayersIdNumber\b/gi, "the provider's Tax ID"],
  [/portalUsername(?:`?\s+or\s+`?portalPassword)\b/gi, 'the payer portal credentials'],
  [/portalPassword(?:`?\s+or\s+`?portalUsername)\b/gi, 'the payer portal credentials'],
  [/portalPassword\b/gi, 'the payer portal password'],
  [/portalUsername\b/gi, 'the payer portal username'],
  // Underscores are word characters, so the \bstedi\b vendor pass below
  // can't catch this token — historical check docs store it verbatim.
  [/\bSTEDI_API_KEY\b/g, 'the eligibility service API key'],
]

/**
 * Scrub vendor-flavored content out of a user-facing error string:
 * markdown links keep their label but lose the URL, bare URLs are removed,
 * raw API field names become plain language, and vendor names are replaced
 * with a neutral phrase.
 */
export function scrubVendorText(text: string): string
export function scrubVendorText(text: string | null | undefined): string | undefined
export function scrubVendorText(text: string | null | undefined): string | undefined {
  if (text == null) return undefined
  let out = text
  // Markdown links: keep the label, drop the URL
  out = out.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
  // Bare URLs
  out = out.replace(/https?:\/\/[^\s)]+/gi, '')
  // Protocol-less vendor domains ("www.stedi.com/docs") — must run after the
  // bare-URL pass so it only sees what that pass left behind; without this,
  // the vendor-name pass would mangle them into "www.the eligibility service.com"
  out = out.replace(/\b(?:www\.)?(?:stedi|uipath)\.[a-z]{2,}(?:\/\S*)?/gi, '')
  for (const [pattern, replacement] of FIELD_NAME_REPLACEMENTS) {
    out = out.replace(pattern, replacement)
  }
  // Vendor-support hand-offs point to our support, not the vendor's
  out = out.replace(/\b(c)ontact\s+(?:stedi|uipath)\s+support\b/gi,
    (_, c: string) => `${c}ontact VitalRev AI support`)
  out = out.replace(/\bstedi\b/gi, 'the eligibility service')
  out = out.replace(/\buipath\b/gi, 'the bot service')
  // Leftover code backticks and whitespace artifacts from the removals above
  out = out.replace(/`/g, '')
  // "with the `provider.npi`" → "with the the provider's NPI" — collapse the
  // doubled article. Lowercase-only second "the" so real names survive
  // ("the The Hartford plan" is left alone).
  out = out.replace(/\b([Tt]he)\s+the\b/g, '$1')
  out = out.replace(/\s+([.,;:!?])/g, '$1')
  out = out.replace(/[ \t]{2,}/g, ' ')
  // Our injected phrases open sentences lowercase ("…the payer. the
  // eligibility service retried…") — capitalize them at sentence starts only.
  out = out.replace(/(^|[.!?]\s+)the (eligibility service|bot service)/g, '$1The $2')
  out = out.trim()
  // A replacement at the start of the string ("UiPath has no record…" →
  // "the bot service has no record…") leaves a lowercase sentence opener.
  return out.charAt(0).toUpperCase() + out.slice(1)
}
